import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setOnEditType,
    setTypeModalVisibilty,
    addEvaluationsType,
    editEvaluationsType,
    getEvaluationTypeConfig,
    saveEvaluationTypeConfig,
    clearTypeConfig,
} from "../../../components/evaluations/evaluationsSlice";
import { getManagersProfiles } from "../../../components/permissions/permissionsSlice";
import Langs from "../../../app/lang/langs";
import { Col, Row } from "react-bootstrap";
import styles from "../Settings.module.css";
import { ToggleButton } from "primereact/togglebutton";
import { MultiSelect } from "primereact/multiselect";
import './SettingsModal.css';
import { DynamicButton } from "../../../components/buttons/DynamicButton";
import ReportConfigSection, {
    DEFAULT_REPORT_CONFIG,
    buildReportConfigPayload,
} from "../../../components/evaluations/ReportConfigSection";
import { showErrorToast } from "../../../app/utils/helpers";

export default function EvaluationTypeModal(props) {

    const dispatch = useDispatch();

    const visible = useSelector((state) => state.evaluations.typeModal);
    const onEditType = useSelector((state) => state.evaluations.onEditType)
    const types = useSelector((state) => state.evaluations.typesIgnoreActive);
    const teams = useSelector((state) => state.teams.list);
    const typeConfig = useSelector((state) => state.evaluations.typeConfig);
    const typeConfigTiers = useSelector((state) => state.evaluations.typeConfigTiers);
    const typeConfigLoading = useSelector((state) => state.evaluations.typeConfigLoading);
    const managersProfiles = useSelector((state) => state.permissions.managersProfiles);

    const [currentType, setCurrentType] = useState(null)
    const [title, setTitle] = useState('')
    const [active, setActive] = useState(false)
    const [associatedTeams, setAssociatedTeams] = useState([])
    const [titleClass, setTitleClass] = useState(styles.rowShadeGreen)
    const [reportConfig, setReportConfig] = useState(DEFAULT_REPORT_CONFIG)
    const [reportTiers, setReportTiers] = useState([])

    const handleClose = () => {
        dispatch(setTypeModalVisibilty(false));
        setAssociatedTeams()
        setTitle('')
        setActive(false)
        setCurrentType(null)
        setTitleClass(styles.rowShadeGreen)
        setReportConfig(DEFAULT_REPORT_CONFIG)
        setReportTiers([])
        dispatch(setOnEditType(null))
        dispatch(clearTypeConfig())
    }

    useEffect(() => {
        const type = types.find(item => item.id == onEditType);
        if (type) {
            setCurrentType(type)
        }
    }, [visible, onEditType, types, teams])

    useEffect(() => {
        if (currentType) {
            setTitle(currentType.title)
            setActive(currentType.active)
            if (teams) {
                setAssociatedTeams(currentType.associated_teams);
            }
        }
    }, [currentType])

    // Manager profiles feed the ONLY/EXCEPT filter — fetch once when the modal opens.
    useEffect(() => {
        if (visible && !managersProfiles.length) {
            dispatch(getManagersProfiles({}))
        }
    }, [visible])

    // Editing an existing type: pull its saved report rules. A brand new type has none,
    // so the form keeps its defaults.
    useEffect(() => {
        if (visible && onEditType) {
            dispatch(getEvaluationTypeConfig({ idType: onEditType }))
        }
    }, [visible, onEditType])

    useEffect(() => {
        if (!typeConfig) return
        setReportConfig({
            member_source: typeConfig.member_source ?? DEFAULT_REPORT_CONFIG.member_source,
            manager_profile_mode: typeConfig.manager_profile_mode ?? DEFAULT_REPORT_CONFIG.manager_profile_mode,
            id_manager_profile: typeConfig.id_manager_profile ?? "",
            exclude_team_managers: Number(typeConfig.exclude_team_managers) === 1,
            eligibility_source: typeConfig.eligibility_source ?? DEFAULT_REPORT_CONFIG.eligibility_source,
            // null means "no rule" and must stay an empty field, not a 0.
            leaves_threshold: typeConfig.leaves_threshold == null ? "" : String(parseFloat(typeConfig.leaves_threshold)),
            leaves_comparator: typeConfig.leaves_comparator ?? DEFAULT_REPORT_CONFIG.leaves_comparator,
            absences_threshold: typeConfig.absences_threshold == null ? "" : String(parseInt(typeConfig.absences_threshold)),
            team_impact_enabled: Number(typeConfig.team_impact_enabled) === 1,
        })
    }, [typeConfig])

    useEffect(() => {
        setReportTiers((typeConfigTiers || []).map(tier => ({
            missed_count: String(tier.missed_count),
            amount: String(parseFloat(tier.amount)),
        })))
    }, [typeConfigTiers])

    /**
     * Saves the report rules for an existing type. Returns false when the form is
     * invalid, so the caller can keep the modal open instead of silently discarding it.
     */
    const saveReportConfig = (idType) => {
        const { valid, errors, body } = buildReportConfigPayload(idType, reportConfig, reportTiers)
        if (!valid) {
            showErrorToast(<Langs str={errors[0]} />)
            return false
        }
        dispatch(saveEvaluationTypeConfig(body))
        return true
    }

    const handleAdd = () => {
        if (title == null || title.trim() == '') {
            setTitleClass(styles.rowShadeRed)
            return
        }

        // Validate the report rules before creating anything, so a bad prime schedule
        // can't leave a type created but unconfigured.
        const { valid, errors } = buildReportConfigPayload(0, reportConfig, reportTiers)
        if (!valid) {
            showErrorToast(<Langs str={errors[0]} />)
            return
        }

        let addBody = {
            title: title,
            active: active,
            rank: types.length,
            added_teams: associatedTeams || []
        }

        dispatch(addEvaluationsType(addBody)).then((res) => {
            // addEvaluationsType returns the new id so the report configuration can be
            // saved in the same interaction rather than making the admin reopen the type.
            const newId = res.payload?.responseData?.id
            if (newId) saveReportConfig(newId)
            handleClose()
        })
    }

    function analyzeTeamChanges(currentTeams, newTeams) {
        const removedTeams = currentTeams.filter(team => !newTeams.includes(team));
        const addedTeams = newTeams.filter(team => !currentTeams.includes(team));
        return [addedTeams, removedTeams];
    }

    const handleEdit = () => {
        if (title == null || title.trim() == '') {
            setTitleClass(styles.rowShadeRed)
            return
        }

        // Report rules first: a rejected prime schedule must not leave the title saved
        // and the configuration silently dropped.
        if (!saveReportConfig(currentType.id)) return

        let teamsChange = analyzeTeamChanges(currentType.associated_teams, associatedTeams)
        let editBody = {
            id: currentType.id,
            title: title,
            active: active,
            rank: currentType.rank,
            added_teams: teamsChange[0],
            removed_teams: teamsChange[1]
        }
        dispatch(editEvaluationsType(editBody));
        handleClose()
    }

    //html

    const header = () => {
        if (onEditType) {
            return (
                <div>
                    <Langs str='EDIT_TYPE' />
                </div>
            )
        }
        return (
            <div>
                <Langs str='ADD_TYPE' />
            </div>
        )
    }

    const body = () => {
        return (
            <div className="p-2">
                <Row className="mt-2">
                    <Row className={`p-2 pt-3 pb-3 border-top`}>
                        <Col lg={3} className="my-auto fw-bold">
                            <Langs str='TITLE' />
                        </Col>
                        <Col lg={9} className="py-2">
                            <input
                                className={`form-control ${titleClass}`}
                                value={title}
                                rows={4}
                                onChange={(e) => { setTitle(e.target.value) }}
                            />
                        </Col>
                    </Row>

                    <Row className="mt-2 p-2 pb-3">
                        <Col lg={3} className="my-3 fw-bold">
                            <Langs str='Associated teams' />
                        </Col>
                        <Col lg={9} className="d-flex justify-content-end">
                            <MultiSelect
                                value={associatedTeams}
                                onChange={(e) => setAssociatedTeams(e.value)}
                                options={teams}
                                optionLabel="label"
                                display="chip"
                                filter
                                placeholder="All"
                                className='customMultiselect'
                            />
                        </Col>
                    </Row>
                    <Row className="mt-2 p-2">
                        <Col lg={3} className="py-2 fw-bold">
                            <Langs str='VISIBLTY' />
                        </Col>
                        <Col lg={9} className="d-flex justify-content-start">
                            <ToggleButton
                                onLabel='Actif'
                                offLabel='Inactif'
                                onIcon="pi pi-check"
                                offIcon="pi pi-times"
                                checked={active}
                                onChange={(e) => setActive(e.value)} className="w-9rem" />
                        </Col>
                    </Row>
                </Row>

                {/* Report & prime rules — what makes this type produce a prime report
                    without a code change. */}
                <ReportConfigSection
                    config={reportConfig}
                    tiers={reportTiers}
                    managersProfiles={managersProfiles}
                    onConfigChange={setReportConfig}
                    onTiersChange={setReportTiers}
                    disabled={typeConfigLoading}
                />
            </div>
        )
    }
    const footer = () => {
        if (onEditType) {
            return (
                <Row className="d-flex justify-content-center border-top pt-3">
                    <Col lg={3}>
                        <DynamicButton
                            onClick={() => handleEdit()}
                            className=''
                            title={<Langs str="EDIT" />}
                            active={true}
                            color="blue"
                        />
                    </Col>
                </Row>
            )
        }
        if (!onEditType) {
            return (
                <Row className="d-flex justify-content-center border-top pt-3">
                    <Col lg={3}>
                        <DynamicButton
                            onClick={() => handleAdd()}
                            className=''
                            title={<Langs str="ADD" />}
                            active={true}
                            color="green"
                        />
                    </Col>
                </Row>
            )
        }
    }


    return (
        <div>
            <Dialog
                header={header}
                footer={footer}
                visible={visible}
                style={{ width: '50vw' }}
                draggable={false}
                onHide={() => { if (!visible) return; handleClose() }}>
                {body()}
            </Dialog>
        </div>
    )
}